<template>
    <div id="content">
        <div class="container-fluid p-0">
            <div class="my-4">
                <!-- Header bar -->
                <div class="spheres-header">
                    <div class="spheres-title-block">
                        <h2 class="spheres-title">{{ $t('spheres.list.title') }}</h2>
                        <p v-if="!loading && spheres.length" class="spheres-subtitle">
                            {{ spheres.length }} {{ pluralizeSphere(spheres.length) }}
                        </p>
                    </div>
                    <router-link to="/spheres/new" class="btn-create">
                        <i class="bi bi-plus-lg"></i>
                        <span>{{ $t('spheres.list.createButton') }}</span>
                    </router-link>
                </div>

                <!-- Loading -->
                <div v-if="loading" class="spheres-loading">
                    <div class="spinner-border" role="status"></div>
                    <p>{{ $t('spheres.list.loading') }}</p>
                </div>

                <!-- Empty state -->
                <div v-else-if="!spheres.length" class="empty-state">
                    <div class="empty-icon">
                        <i class="bi bi-collection"></i>
                    </div>
                    <h4 class="empty-title">{{ $t('spheres.list.empty.title') }}</h4>
                    <p class="empty-text">{{ $t('spheres.list.empty.text') }}</p>
                    <router-link to="/spheres/new" class="btn-create">
                        <i class="bi bi-plus-lg"></i>
                        <span>{{ $t('spheres.list.empty.createFirst') }}</span>
                    </router-link>
                </div>

                <!-- Sphere list -->
                <div v-else class="sphere-list">
                    <router-link
                        v-for="s in spheres"
                        :key="s.id"
                        :to="'/spheres/' + s.id"
                        class="sphere-row"
                    >
                        <div class="sphere-row-icon">
                            <img
                                v-if="sphereIconSrc(s)"
                                :src="sphereIconSrc(s)"
                                :alt="s.icon_slug || 'icon'"
                            />
                            <i v-else class="bi bi-circle"></i>
                        </div>
                        <div class="sphere-row-main">
                            <div class="sphere-row-titleline">
                                <span class="sphere-row-name">{{ s.name }}</span>
                                <span class="sphere-row-slug">{{ s.slug }}</span>
                                <span
                                    class="sphere-status"
                                    :class="s.enabled ? 'sphere-status-on' : 'sphere-status-off'"
                                >
                                    <span class="sphere-status-dot"></span>
                                    {{ s.enabled ? $t('spheres.list.status.active') : $t('spheres.list.status.disabled') }}
                                </span>
                            </div>
                            <p class="sphere-row-desc">{{ s.description || $t('spheres.list.noDescription') }}</p>
                            <div class="sphere-row-meta">
                                <span class="meta-badge">
                                    <i class="bi bi-list-ul"></i>
                                    {{ countPreferences(s) }} {{ pluralizePref(countPreferences(s)) }}
                                </span>
                                <span class="meta-badge">
                                    <i class="bi bi-grid-3x3-gap"></i>
                                    {{ s.widgets ? s.widgets.length : 0 }} {{ pluralizeWidget(s.widgets ? s.widgets.length : 0) }}
                                </span>
                                <span class="meta-badge">
                                    <i class="bi bi-arrow-down-up"></i>
                                    {{ $t('spheres.list.position', { n: s.position || 0 }) }}
                                </span>
                            </div>
                        </div>
                        <div class="sphere-row-arrow">
                            <i class="bi bi-chevron-right"></i>
                        </div>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Spheres',
    data: () => ({
        spheres: [],
        loading: true
    }),
    async mounted() {
        this.$emit('setPageTitle', this.$t('spheres.list.title'))
        await this.load()
    },
    methods: {
        async load() {
            this.loading = true
            const r = await this.$api.getSpheres()
            if (r.result === 'SUCCESS') {
                this.spheres = r.data
            } else {
                this.$notify({ text: r.msg, type: 'error' })
                this.spheres = []
            }
            this.loading = false
        },
        sphereIconSrc(sphere) {
            if (sphere.icon_url) return sphere.icon_url
            if (sphere.icon_slug) return '/icons/spheres/' + sphere.icon_slug + '.svg'
            return null
        },
        countPreferences(sphere) {
            if (!sphere.preferences) return 0
            let n = sphere.preferences.length
            for (const p of sphere.preferences) {
                n += (p.children ? p.children.length : 0)
            }
            return n
        },
        pluralizeSphere(n) {
            const mod10 = n % 10
            const mod100 = n % 100
            if (mod10 === 1 && mod100 !== 11) return this.$t('spheres.list.plural.sphere.one')
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return this.$t('spheres.list.plural.sphere.few')
            return this.$t('spheres.list.plural.sphere.many')
        },
        pluralizePref(n) {
            const mod10 = n % 10
            const mod100 = n % 100
            if (mod10 === 1 && mod100 !== 11) return this.$t('spheres.list.plural.preference.one')
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return this.$t('spheres.list.plural.preference.few')
            return this.$t('spheres.list.plural.preference.many')
        },
        pluralizeWidget(n) {
            const mod10 = n % 10
            const mod100 = n % 100
            if (mod10 === 1 && mod100 !== 11) return this.$t('spheres.list.plural.widget.one')
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return this.$t('spheres.list.plural.widget.few')
            return this.$t('spheres.list.plural.widget.many')
        }
    }
}
</script>

<style scoped>
/* Header */
.spheres-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;
}
.spheres-title-block {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.spheres-title {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    line-height: 1.2;
}
.spheres-subtitle {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}

/* Create button */
.btn-create {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #7c3aed;
    color: #fff;
    padding: 10px 18px;
    border-radius: 10px;
    border: 1px solid #7c3aed;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.15s ease;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(124, 58, 237, 0.2);
}
.btn-create:hover {
    background: #6d28d9;
    border-color: #6d28d9;
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
    color: #fff;
    transform: translateY(-1px);
}
.btn-create i {
    font-size: 16px;
}

/* Loading */
.spheres-loading {
    text-align: center;
    padding: 64px 20px;
    color: #64748b;
}
.spheres-loading .spinner-border {
    color: #7c3aed;
    width: 2rem;
    height: 2rem;
}
.spheres-loading p {
    margin-top: 16px;
    font-size: 14px;
    color: #64748b;
}

/* Empty state */
.empty-state {
    background: #fff;
    border: 1px dashed #cbd5e1;
    border-radius: 14px;
    padding: 56px 24px;
    text-align: center;
}
.empty-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    background: #f5f0ff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7c3aed;
    font-size: 24px;
}
.empty-title {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 6px 0;
}
.empty-text {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 20px 0;
}

/* Sphere row list */
.sphere-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.sphere-row {
    display: flex;
    align-items: center;
    gap: 16px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 16px 20px;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.sphere-row:hover {
    border-color: #c4b5fd;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
    color: inherit;
}
.sphere-row:hover .sphere-row-arrow {
    color: #7c3aed;
    transform: translateX(2px);
}

.sphere-row-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #faf7ff;
    border: 1px solid #ede9fe;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.sphere-row-icon img {
    width: 36px;
    height: 36px;
    object-fit: contain;
}
.sphere-row-icon i {
    font-size: 22px;
    color: #c4b5fd;
}

.sphere-row-main {
    flex: 1;
    min-width: 0;
}
.sphere-row-titleline {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 4px;
    flex-wrap: wrap;
}
.sphere-row-name {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
}
.sphere-row-slug {
    font-size: 11px;
    font-family: ui-monospace, monospace;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 4px;
}
.sphere-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.sphere-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}
.sphere-status-on {
    background: #dcfce7;
    color: #15803d;
}
.sphere-status-on .sphere-status-dot {
    background: #22c55e;
}
.sphere-status-off {
    background: #f1f5f9;
    color: #64748b;
}
.sphere-status-off .sphere-status-dot {
    background: #94a3b8;
}

.sphere-row-desc {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 8px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
}
.sphere-row-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.meta-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    padding: 3px 9px;
    border-radius: 6px;
}
.meta-badge i {
    font-size: 11px;
    color: #7c3aed;
}

.sphere-row-arrow {
    color: #cbd5e1;
    font-size: 18px;
    transition: color 0.15s ease, transform 0.15s ease;
    flex-shrink: 0;
}

@media (max-width: 640px) {
    .spheres-header {
        flex-direction: column;
        align-items: stretch;
    }
    .btn-create {
        justify-content: center;
    }
    .sphere-row {
        flex-wrap: wrap;
    }
    .sphere-row-arrow {
        display: none;
    }
}
</style>
